import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ENV_CONFIG from '../config/environment-unified';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
`;

const RoleHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  text-align: center;
`;

const RoleTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 600;
`;

const RoleDescription = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: 16px;
`;

const QuickStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #4299e1;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  color: #718096;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActionSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  color: #2d3748;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const ActionCard = styled.div`
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;

  &:hover {
    border-color: #4299e1;
    background: #f7fafc;
  }
`;

const ActionIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const ActionTitle = styled.div`
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
`;

const ActionDescription = styled.div`
  font-size: 14px;
  color: #718096;
`;

const AssignmentsList = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const AssignmentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
`;

const EventInfo = styled.div`
  flex: 1;
`;

const EventName = styled.div`
  font-weight: 600;
  color: #2d3748;
`;

const EventDate = styled.div`
  font-size: 14px;
  color: #718096;
`;

const RoleTag = styled.span`
  background: #4299e1;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  text-transform: uppercase;
`;

const RoleDashboard = () => {
  const history = useHistory();
  const user = useSelector(state => state.user.data);
  const [stats, setStats] = useState({});
  const [assignments, setAssignments] = useState([]);
  // const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      // setLoading(true);

      // In this app, "organizer" is often event-level (event.createdBy) rather than an account role.
      // Always fetch organizer stats for the current user id (it will be 0 if they created no events).
      await fetchOrganizerData();

      // Fetch user-specific data based on role (merge into stats)
      if (user.role === 'streamer') {
        await fetchStreamerData();
      } else if (['admin', 'moderator'].includes(user.role)) {
        await fetchAdminData();
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erreur lors du chargement du tableau de bord');
    } finally {
      // setLoading(false);
    }
  };

  const fetchStreamerData = async () => {
    try {
      // Get streamer assignments
      const response = await axios.get(`${ENV_CONFIG.API_BASE_URL}/api/assignments/streamer/${user.id}`, {
        withCredentials: true
      });

      setAssignments(response.data.assignments || []);
      setStats((prev) => ({
        ...prev,
        assignedEvents: response.data.assignments?.length || 0,
        upcomingStreams: response.data.assignments?.filter(a =>
          new Date(a.event.startDate) > new Date()
        ).length || 0,
        totalStreams: response.data.assignments?.length || 0
      }));
    } catch (error) {
      console.error('Error fetching streamer data:', error);
    }
  };

  const fetchOrganizerData = async () => {
    try {
      // Ticket-service: organizer sales + gross revenue (GCOIN)
      const [orgStatsRes, payoutSettingsRes] = await Promise.all([
        axios.get(`${ENV_CONFIG.API_BASE_URL}/api/tickets/organizer/${user.id}/stats`, { withCredentials: true, timeout: 8000 }),
        // Use wallet payout settings as "platform fee %" (already configurable in DB via payout_settings)
        axios.get(`${ENV_CONFIG.API_BASE_URL}/api/wallet/payout/settings`, { withCredentials: true, timeout: 8000 })
      ]);

      const createdEvents = Number(orgStatsRes?.data?.createdEvents || 0);
      const activeEvents = Number(orgStatsRes?.data?.activeEvents || 0);
      const paidTickets = Number(orgStatsRes?.data?.paidTickets || 0);
      const grossRevenue = Number(orgStatsRes?.data?.grossRevenue || 0);

      /**
       * KPI: PLATFORM FEE PCT
       * - Percentage taken by the platform from the gross revenue.
       * - Source: wallet payout settings (`default_platform_fee_percentage`).
       * - Note: backend may return it as string/Decimal, so we normalize defensively.
       */
      const platformFeePct = (() => {
        const raw = payoutSettingsRes?.data?.default_platform_fee_percentage ?? 0;
        const norm = String(raw).replace(',', '.');
        const n = parseFloat(norm);
        return Number.isFinite(n) ? n : 0;
      })();

      /**
       * KPI: PLATFORM FEE AMOUNT
       * - Absolute platform fee in GCOIN, computed from gross revenue:
       *   platformFeeAmount = grossRevenue * platformFeePct / 100
       */
      const platformFeeAmount = (() => {
        const n = (grossRevenue * platformFeePct) / 100;
        return Number.isFinite(n) ? +n.toFixed(2) : 0;
      })();

      /**
       * KPI: ORGANIZER NET
       * - What the organizer earns after the platform fee:
       *   organizerNet = grossRevenue - platformFeeAmount
       */
      const organizerNet = (() => {
        const n = grossRevenue - platformFeeAmount;
        return Number.isFinite(n) ? +n.toFixed(2) : 0;
      })();

      setStats((prev) => ({
        ...prev,
        createdEvents,
        activeEvents,
        paidTickets,
        grossRevenue,
        // PLATFORM FEE PCT (percentage)
        platformFeePct,
        // PLATFORM FEE AMOUNT (GCOIN)
        platformFeeAmount,
        // ORGANIZER NET (GCOIN)
        organizerNet,
        /**
         * KPI: TOTAL REVENUE
         * - In this dashboard we expose a single “revenue” number for the organizer.
         * - We currently define it as the organizer net (after platform fee), not the gross.
         *   totalRevenue = organizerNet
         */
        totalRevenue: organizerNet
      }));
    } catch (error) {
      console.error('Error fetching organizer data:', error);
    }
  };

  const fetchAdminData = async () => {
    try {
      // This would need admin endpoints
      setStats((prev) => ({
        ...prev,
        totalUsers: 0,
        totalEvents: 0,
        totalRevenue: 0
      }));
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
  };

  const getRoleConfig = () => {
    switch (user?.role) {
      case 'viewer':
        return {
          title: 'Tableau de Bord',
          description: 'Vos événements et vos revenus (si vous en avez créé)',
          icon: '👤',
          actions: [
            {
              icon: '➕',
              title: 'Créer Événement',
              description: 'Nouvel événement',
              onClick: () => history.push('/events/create')
            },
            {
              icon: '📅',
              title: 'Mes événements',
              description: 'Voir mes événements',
              onClick: () => history.push('/my-events')
            },
            {
              icon: '💰',
              title: 'Revenus',
              description: 'Voir les gains',
              onClick: () => history.push('/reversements')
            }
          ]
        };

      case 'streamer':
        return {
          title: 'Tableau de Bord Streamer',
          description: 'Gérez vos streams et assignations d\'événements',
          icon: '🎮',
          actions: [
            {
              icon: '📺',
              title: 'Mes Streams',
              description: 'Voir mes assignations',
              onClick: () => { }
            },
            {
              icon: '📊',
              title: 'Statistiques',
              description: 'Performance de streaming',
              onClick: () => { }
            }
          ]
        };

      case 'organizer':
        return {
          title: 'Tableau de Bord Organisateur',
          description: 'Créez et gérez vos événements',
          icon: '🏢',
          actions: [
            {
              icon: '➕',
              title: 'Créer Événement',
              description: 'Nouvel événement',
              onClick: () => history.push('/events/create')
            },
            {
              icon: '🎬',
              title: 'Gérer Streamers',
              description: 'Assigner des streamers',
              onClick: () => { }
            },
            {
              icon: '💰',
              title: 'Revenus',
              description: 'Voir les gains',
              onClick: () => history.push('/reversements')
            }
          ]
        };

      case 'moderator':
        return {
          title: 'Tableau de Bord Modérateur',
          description: 'Modération et support de la plateforme',
          icon: '🛡️',
          actions: [
            {
              icon: '👥',
              title: 'Gérer Utilisateurs',
              description: 'Modération des comptes',
              onClick: () => { }
            },
            {
              icon: '🎫',
              title: 'Accès Gratuit',
              description: 'Tous les événements',
              onClick: () => { }
            }
          ]
        };

      case 'admin':
        return {
          title: 'Tableau de Bord Administrateur',
          description: 'Administration complète de la plateforme',
          icon: '👑',
          actions: [
            {
              icon: '💳',
              title: 'Gestion G-Coins',
              description: 'Créditer/débiter',
              onClick: () => { }
            },
            {
              icon: '📊',
              title: 'Analytics',
              description: 'Statistiques globales',
              onClick: () => { }
            },
            {
              icon: '⚙️',
              title: 'Configuration',
              description: 'Paramètres système',
              onClick: () => { }
            }
          ]
        };

      default:
        return {
          title: 'Tableau de Bord',
          description: 'Bienvenue sur CoreArena',
          icon: '👤',
          actions: []
        };
    }
  };

  if (!user) {
    return <div>Veuillez vous connecter</div>;
  }

  const roleConfig = getRoleConfig();
  const hiddenStatKeys = new Set(['platformFeePct', 'platformFeeAmount', 'organizerNet', 'totalRevenue']);

  return (
    <DashboardContainer>
      <RoleHeader>
        <RoleTitle>
          {roleConfig.icon} {roleConfig.title}
        </RoleTitle>
        <RoleDescription>{roleConfig.description}</RoleDescription>
      </RoleHeader>

      <QuickStatsGrid>
        {Object.entries(stats)
          .filter(([key]) => !hiddenStatKeys.has(key))
          .map(([key, value]) => (
            <StatCard key={key}>
              <StatValue>{value}</StatValue>
              <StatLabel>{key.replace(/([A-Z])/g, ' $1').toLowerCase()}</StatLabel>
            </StatCard>
          ))}
      </QuickStatsGrid>

      {roleConfig.actions.length > 0 && (
        <ActionSection>
          <SectionTitle>🚀 Actions Rapides</SectionTitle>
          <ActionGrid>
            {roleConfig.actions.map((action, index) => (
              <ActionCard key={index} onClick={action.onClick}>
                <ActionIcon>{action.icon}</ActionIcon>
                <ActionTitle>{action.title}</ActionTitle>
                <ActionDescription>{action.description}</ActionDescription>
              </ActionCard>
            ))}
          </ActionGrid>
        </ActionSection>
      )}

      {user.role === 'streamer' && assignments.length > 0 && (
        <AssignmentsList>
          <SectionTitle>🎬 Mes Assignations</SectionTitle>
          {assignments.map((assignment) => (
            <AssignmentItem key={assignment.id}>
              <EventInfo>
                <EventName>{assignment.event.name}</EventName>
                <EventDate>
                  {new Date(assignment.event.startDate).toLocaleDateString('fr-FR')}
                </EventDate>
              </EventInfo>
              <RoleTag>{assignment.role.replace('_', ' ')}</RoleTag>
            </AssignmentItem>
          ))}
        </AssignmentsList>
      )}
    </DashboardContainer>
  );
};

export default RoleDashboard;
